import { 
  Box, 
  Button, 
  Flex, 
  Heading, 
  Text, 
  VStack, 
  HStack, 
  useColorModeValue,
  Alert,
  AlertIcon,
  Center,
} from "@chakra-ui/react";
import React, { useState } from "react";
import PayrollTable from "../../components/tables/PayrollTable";
import { useGetPayroll, useGetAdminPayroll } from "../../services/employeeService";
import { useGetPlanInfo } from "../../services/planService";
import YearSelector from "../../components/yearSelector";
import MonthSelector from "../../components/MonthSelector";
import { CSVLink } from "react-csv";
import { FiDownload, FiCalendar } from "react-icons/fi";
import { useUserContext } from "../../context/UserContext";
import { useAccessControl } from "../../hooks/useAccessControl";
import BranchFilter from "../../components/BranchFilter";
import BranchAccessRestriction from "../../components/BranchAccessRestriction";

const PayrollSheet = () => {
  const [data, setData] = useState({ year: "", month: "" });
  const [selectedBranch, setSelectedBranch] = useState("all");
  
  // Get user context and access control
  const { user } = useUserContext();
  const { 
    canAccessPayroll,
    canAccessPayrollFeature,
    isCompanyAdmin 
  } = useAccessControl();
  
  const isAdmin = isCompanyAdmin();
  const canAccessReports = canAccessPayrollFeature('payroll_reports');
  
  // Get plan info for currency
  const { planInfo } = useGetPlanInfo();
  
  // Company admins: use admin endpoints with branch filtering
  const adminPayrollData = useGetAdminPayroll({ 
    enabled: isAdmin,
    branch_id: selectedBranch
  });
  
  // Payroll roles: use regular endpoints (no branch filtering needed)
  const regularPayrollData = useGetPayroll({ 
    enabled: !isAdmin && canAccessReports
  });
  
  // Select appropriate data based on user role
  const payrollIsLoading = isAdmin 
    ? adminPayrollData.adminPayrollIsLoading 
    : regularPayrollData.payrollIsLoading;
    
  const payrollData = isAdmin 
    ? adminPayrollData.adminPayrollData 
    : regularPayrollData.payrollData;
    
  const payrollSetFilter = isAdmin 
    ? adminPayrollData.adminPayrollSetFilter 
    : regularPayrollData.payrollSetFilter;
  
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const handleSearch = () => {
    const payload = {
      year: data?.year?.value,
      month: data?.month?.value
    };
    
    // Add branch filter for company admins
    if (isAdmin && selectedBranch && selectedBranch !== 'all') {
      payload.branch_id = selectedBranch;
    }
    
    payrollSetFilter(payload);
  };

  // Access control - check if user can access payroll reports
  if (!canAccessPayroll() || !canAccessReports) {
    return (
      <Box w="100%" bg="gray.50" minH="100vh" p={6}>
        <BranchAccessRestriction featureName="Payroll Reports" />
      </Box>
    );
  }

  return (
    <Box w="100%" bg="gray.50" minH="100vh" p={6}>
      {/* Compact Header with Filters */}
      <Box
        bg={bgColor}
        borderRadius="12px"
        p={4}
        shadow="sm"
        border="1px solid"
        borderColor={borderColor}
        mb={4}
      >
        <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={4}>
          <Heading size="md" color="gray.900" fontWeight="600">
            Payroll Reports
          </Heading>
          <HStack spacing={3} flexWrap="wrap">
            <BranchFilter
              selectedBranch={selectedBranch}
              onBranchChange={setSelectedBranch}
              showLabel={false}
              maxW="200px"
            />
            <Box w="140px">
              <MonthSelector onChange={(value) => setData({ ...data, month: value })} />
            </Box>
            <Box w="120px">
              <YearSelector onChange={(value) => setData({ ...data, year: value })} />
            </Box>
            <Button
              leftIcon={<FiCalendar />}
              colorScheme="blue"
              variant="solid"
              onClick={handleSearch}
              size="md"
              borderRadius="8px"
            >
              Generate
            </Button>
            <CSVLink data={payrollData} onClick={(e) => e.stopPropagation()}>
              <Button
                leftIcon={<FiDownload />}
                colorScheme="green"
                variant="outline"
                size="md"
                borderRadius="8px"
              >
                Export CSV
              </Button>
            </CSVLink>
          </HStack>
        </Flex>
      </Box>

      {/* Payroll Table */}
      <Box
        bg={bgColor}
        borderRadius="16px"
        shadow="sm"
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <PayrollTable employeeData={payrollData} loading={payrollIsLoading} />
      </Box>
    </Box>
  );
};

export default PayrollSheet;